import {
    ChatFieldsFragment,
    useGetChatByClientAndClinicQuery,
} from '~graphql/generated/graphql'
import { useClinic } from '~contexts/clinic-context'
import { SimpleClient } from '~utils/shared-types'

interface UseChatForClientOptions {
    client: SimpleClient
    /** Override the clinic used for chat lookup (e.g. enterprise contexts). */
    clinicId?: string
}

interface UseChatForClientResult {
    chat: ChatFieldsFragment | null | undefined
    loading: boolean
    clinicId: string | undefined
}

/**
 * Looks up the existing chat for a given client + clinic. Returns null if
 * no chat exists yet (callers should disable any "open chat" affordances
 * in that case). Used both by the OpenChatButton and any other place that
 * needs to find a chat anchored to a client.
 */
export function useChatForClient({
    client,
    clinicId: clinicIdOverride,
}: UseChatForClientOptions): UseChatForClientResult {
    const { clinic } = useClinic()
    const resolvedClinicId = clinicIdOverride || clinic?.id

    const { data, loading } = useGetChatByClientAndClinicQuery({
        variables: {
            clientId: client.id,
            clinicId: resolvedClinicId || '',
        },
        skip: !resolvedClinicId,
    })

    return {
        chat: data?.getChatByClientAndClinic,
        loading,
        clinicId: resolvedClinicId,
    }
}
